import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

const BookingTrendChart = (meetings = [], weekStart = null, weekEnd = null) => {
  const now = dayjs();
  const startOfWeek = weekStart ? dayjs(weekStart) : now.startOf('week');
  const endOfWeek = weekEnd ? dayjs(weekEnd) : now.endOf('week');

  const xData = ['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekdayMap = { 0: 'Sun', 1: 'Mon', 2: 'Tues', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };

  const TIME_BUCKETS = {
    Morning: [6, 12],
    Afternoon: [12, 17],
    Evening: [17, 21],
    Night: [21, 24]
  };

  const initializeCounts = () => ({
    Morning: 0,
    Afternoon: 0,
    Evening: 0,
    Night: 0
  });

  const weekDayCounts = Object.fromEntries(xData.map(day => [day, initializeCounts()]));

  const getMeetingDateTimes = (meeting, offsetDays = 0) => {
    const dateBase = dayjs(meeting.startDate || meeting.startTime);
    if (!dateBase.isValid()) return {};

    const baseDate = dateBase.add(offsetDays, 'day').startOf('day');

    const [startHour = 0, startMin = 0] = (meeting.startTime || '00:00').split(':').map(Number);
    const [endHour = 0, endMin = 0] = (meeting.endTime || '00:00').split(':').map(Number);

    const start = baseDate.clone().hour(startHour).minute(startMin);
    const end = baseDate.clone().hour(endHour).minute(endMin);

    return { start, end };
  };

  const expandRecurringMeetings = (meeting, count = 4) => {
    const recurrence = String(meeting.reoccurrence || '').toLowerCase();
    const { start, end } = getMeetingDateTimes(meeting);
    if (!start?.isValid() || !end?.isValid()) return [];

    const duration = end.diff(start, 'minute');
    const generateInstance = (offsetDays) => {
      const startInst = start.clone().add(offsetDays, 'day');
      const endInst = startInst.clone().add(duration, 'minute');
      return {
        ...meeting,
        startTime: startInst.toISOString(),
        endTime: endInst.toISOString()
      };
    };

    if (recurrence === 'daily' || recurrence === '1') {
      return Array.from({ length: count + 1 }, (_, i) => generateInstance(i));
    } else if (recurrence === 'weekly' || recurrence === '2') {
      return Array.from({ length: count + 1 }, (_, i) => generateInstance(i * 7));
    }

    return [{
      ...meeting,
      startTime: start.toISOString(),
      endTime: end.toISOString()
    }];
  };

  const allExpandedMeetings = meetings.flatMap(meeting => expandRecurringMeetings(meeting, 4));

  allExpandedMeetings.forEach(meeting => {
    const start = dayjs(meeting.startTime);
    if (!start.isValid() || !start.isSameOrAfter(startOfWeek) || !start.isSameOrBefore(endOfWeek)) return;

    const day = weekdayMap[start.day()];
    const hour = start.hour();

    let bucket = '';
    if (hour >= 6 && hour < 12) bucket = 'Morning';
    else if (hour >= 12 && hour < 17) bucket = 'Afternoon';
    else if (hour >= 17 && hour < 21) bucket = 'Evening';
    else bucket = 'Night';

    if (weekDayCounts[day]) {
      weekDayCounts[day][bucket]++;
    }
  });

  const timeBucketKeys = Object.keys(TIME_BUCKETS);

  const series = timeBucketKeys.map(bucket => ({
    name: bucket,
    type: 'bar',
    stack: 'time',
    barWidth: 25,
    data: xData.map(day => {
      const value = weekDayCounts[day][bucket];
      const lastVisibleBucket = timeBucketKeys
        .slice()
        .reverse()
        .find(b => weekDayCounts[day][b] > 0);
      const isTop = bucket === lastVisibleBucket;
      return {
        value,
        itemStyle: isTop ? { borderRadius: [4, 4, 0, 0] } : {}
      };
    })
  }));

  const totalPerDay = xData.map(day =>
    timeBucketKeys.reduce((sum, bucket) => sum + weekDayCounts[day][bucket], 0)
  );

  series.push({
    name: 'Total',
    type: 'line',
    data: totalPerDay,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: { width: 2, color: '#1DBA83' },
    itemStyle: { color: '#1DBA83' },
    tooltip: {
      valueFormatter: value => `${value} meetings`
    },
    z: 10
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: { show: false },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontWeight: 'bold' }
    },
    yAxis: {
      type: 'value',
      name: 'Meetings'
    },
    grid: {
      left: 10,
      top: 30,
      right: 10,
      bottom: 50,
      containLabel: true
    },
    series
  };
};

export default BookingTrendChart;
